import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

function hasTipPayload(payload) {
  if (payload == null) return false;
  if (typeof payload === 'string') return Boolean(payload.trim());
  if (typeof payload === 'object') {
    return Boolean(payload.title || payload.text || (Array.isArray(payload.rows) && payload.rows.length));
  }
  return Boolean(String(payload).trim());
}

function normalizeTipPayload(payload) {
  if (!hasTipPayload(payload)) return null;
  if (typeof payload === 'string') return { mode: 'text', text: payload };
  if (typeof payload === 'object' && (payload.title || Array.isArray(payload.rows))) {
    return {
      mode: 'rich',
      title: payload.title || '',
      rows: Array.isArray(payload.rows) ? payload.rows.filter(Boolean) : [],
      text: payload.title || payload.text || '',
    };
  }
  return { mode: 'text', text: String(payload) };
}

/**
 * Tooltip flutuante (visual = bch-pill-label), fora do overflow dos cards.
 * Aceita string ou { title, rows: [{ label, value }] }.
 */
export function BchTipLayer({ tip }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);
  const payload = tip ? normalizeTipPayload(tip.payload ?? tip.text) : null;

  useLayoutEffect(() => {
    if (!tip) {
      setPos((prev) => (prev == null ? prev : null));
      return undefined;
    }
    const el = ref.current;
    if (!el) return undefined;
    const w = el.offsetWidth || 0;
    const half = w / 2;
    const pad = 8;
    const nextX = Math.min(Math.max(tip.x, pad + half), window.innerWidth - pad - half);
    const nextY = tip.y;
    const nextPlace = tip.place || 'below';
    setPos((prev) => {
      if (prev && prev.x === nextX && prev.y === nextY && prev.place === nextPlace) return prev;
      return { x: nextX, y: nextY, place: nextPlace };
    });
    return undefined;
  }, [tip]);

  if (!payload || !tip) return null;

  const style = pos
    ? { left: pos.x, top: pos.y }
    : { left: tip.x, top: tip.y, visibility: 'hidden' };
  const place = pos?.place || tip.place || 'below';
  const isRich = payload.mode === 'rich';

  return createPortal(
    <span
      ref={ref}
      className={`bch-float-tip bch-float-tip--${place}${isRich ? ' bch-float-tip--rich' : ''}`}
      style={style}
      role="tooltip"
    >
      {isRich ? (
        <>
          {payload.title ? <span className="bch-float-tip-title">{payload.title}</span> : null}
          {payload.rows.map((row, idx) => (
            <span className="bch-float-tip-row" key={`${row.label || 'r'}-${idx}`}>
              {row.label ? <span className="bch-float-tip-k">{row.label}</span> : null}
              <span className="bch-float-tip-v">{row.value}</span>
            </span>
          ))}
        </>
      ) : (
        payload.text
      )}
    </span>,
    document.body
  );
}

export function useBchTip() {
  const [tip, setTip] = useState(null);

  const hide = useCallback(() => setTip(null), []);

  const show = useCallback((el, payload, place = 'below') => {
    if (!el || !hasTipPayload(payload)) return;
    const r = el.getBoundingClientRect();
    const x = r.left + r.width / 2;
    const y = place === 'above' ? r.top - 6 : r.bottom + 6;
    setTip({ payload, x, y, place });
  }, []);

  const tipProps = useCallback(
    (payload, place = 'below') => ({
      onMouseEnter: (e) => show(e.currentTarget, payload, place),
      onMouseLeave: hide,
      onFocus: (e) => show(e.currentTarget, payload, place),
      onBlur: hide,
    }),
    [show, hide]
  );

  return { tip, tipProps, hideTip: hide };
}
